import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION } from "@/version";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// App Store URLs — update these once the app is live
const APP_STORE_URL = "https://apps.apple.com/app/idYOUR_APP_ID";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.villarealgroup.app";

/** Compare two semver strings. Returns true if `a` is strictly less than `b`. */
function isOlderThan(a: string, b: string): boolean {
  const parse = (v: string) => v.split(".").map((n) => parseInt(n, 10) || 0);
  const [aMaj, aMin, aPat] = parse(a);
  const [bMaj, bMin, bPat] = parse(b);
  if (aMaj !== bMaj) return aMaj < bMaj;
  if (aMin !== bMin) return aMin < bMin;
  return aPat < bPat;
}

export function AppUpdateGate() {
  const [updateRequired, setUpdateRequired] = useState(false);

  useEffect(() => {
    // Only run inside the native app — never on web
    if (!Capacitor.isNativePlatform()) return;

    (async () => {
      try {
        const { data } = await supabase
          .from("app_config")
          .select("value")
          .eq("key", "min_app_version")
          .maybeSingle();
        const minVersion = (data as any)?.value as string | undefined;
        if (minVersion && isOlderThan(APP_VERSION, minVersion)) {
          setUpdateRequired(true);
        }
      } catch {
        // Network failure — allow the app to proceed
      }
    })();
  }, []);

  if (!updateRequired) return null;

  const storeUrl =
    Capacitor.getPlatform() === "android" ? PLAY_STORE_URL : APP_STORE_URL;

  return (
    <AlertDialog open>
      <AlertDialogContent
        className="max-w-sm"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl">
            Update Required
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-relaxed">
            A new version of Villa Real Group is available with important
            improvements. Please update to continue using the app.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            className="w-full"
            onClick={() => window.open(storeUrl, "_system")}
          >
            Update Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
